import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import {
  CUSTOM_STRATEGY_STATUS_COLOR,
  CUSTOM_STRATEGY_STATUS_LABEL,
  CustomStrategyStatus,
} from "constants/strategy";
import { StrategyCheckField, StrategyDetailItem, StrategyService } from "service/strategy/service";

const formatKST = (utc: string | null | undefined): string => {
  if (!utc) return "";
  const iso = utc.includes("T")
    ? utc.endsWith("Z")
      ? utc
      : `${utc}Z`
    : `${utc.replace(" ", "T")}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return utc;
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${kst.getUTCFullYear()}-${pad(kst.getUTCMonth() + 1)}-${pad(kst.getUTCDate())} ${pad(
    kst.getUTCHours()
  )}:${pad(kst.getUTCMinutes())}`;
};

interface CheckListItem {
  field: StrategyCheckField;
  label: string;
  icon: string;
  iconColor: string;
}

const CHECK_LIST: CheckListItem[] = [
  { field: "recommend_done", label: "추천물건 선별", icon: "apartment", iconColor: "#f97316" },
  { field: "email_sent", label: "이메일 발송", icon: "mail_outline", iconColor: "#3b82f6" },
  {
    field: "kakao_sent",
    label: "카카오톡 발송",
    icon: "chat_bubble_outline",
    iconColor: "#eab308",
  },
  { field: "sms_sent", label: "SMS 발송", icon: "smartphone", iconColor: "#a855f7" },
  { field: "call_done", label: "전화 완료", icon: "phone", iconColor: "#ec4899" },
];

const STATUS_BUTTON_COLOR: Record<CustomStrategyStatus, { bg: string; fg: string }> = {
  [CustomStrategyStatus.NEW]: { bg: "#e5e7eb", fg: "#374151" },
  [CustomStrategyStatus.CONTACTING]: { bg: "#dbeafe", fg: "#1d4ed8" },
  [CustomStrategyStatus.CONSULTED]: { bg: "#dcfce7", fg: "#15803d" },
  [CustomStrategyStatus.RECOMMENDED]: { bg: "#fef9c3", fg: "#a16207" },
  [CustomStrategyStatus.CONTRACT]: { bg: "#f3e8ff", fg: "#7e22ce" },
  [CustomStrategyStatus.CLOSED]: { bg: "#fee2e2", fg: "#b91c1c" },
};

const STATUS_BUTTONS: CustomStrategyStatus[] = [
  CustomStrategyStatus.NEW,
  CustomStrategyStatus.CONTACTING,
  CustomStrategyStatus.CONSULTED,
  CustomStrategyStatus.RECOMMENDED,
  CustomStrategyStatus.CONTRACT,
  CustomStrategyStatus.CLOSED,
];

const QUESTION_CHIP_COLOR: Record<number, { bg: string; fg: string }> = {
  1: { bg: "#fce7f3", fg: "#be185d" },
  2: { bg: "#f3e8ff", fg: "#7e22ce" },
  3: { bg: "#dbeafe", fg: "#1d4ed8" },
  4: { bg: "#dcfce7", fg: "#15803d" },
};

function StrategyDetail(): JSX.Element | null {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [row, setRow] = useState<StrategyDetailItem | null>(null);
  const [assigneeInput, setAssigneeInput] = useState("");
  const [memoInput, setMemoInput] = useState("");

  const fetchDetail = async () => {
    if (!id) return;
    try {
      const res = await StrategyService.getDetail(id);
      setRow(res);
      setAssigneeInput(res?.assignee_name ?? "");
      setMemoInput(res?.memo ?? "");
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  if (!row) return null;

  const statusLabel = CUSTOM_STRATEGY_STATUS_LABEL[row.status] ?? row.status;
  const statusColor = CUSTOM_STRATEGY_STATUS_COLOR[row.status] ?? "#6b7280";

  const handleAssign = async () => {
    const name = assigneeInput.trim();
    if (!name) return;
    try {
      await StrategyService.updateAssignee(row.strategy_id, name);
      await fetchDetail();
    } catch (err) {
      console.log(err);
      alert("담당자 배정에 실패했습니다.");
    }
  };

  const handleSaveMemo = async () => {
    try {
      await StrategyService.updateMemo(row.strategy_id, memoInput);
      await fetchDetail();
    } catch (err) {
      console.log(err);
      alert("메모 저장에 실패했습니다.");
    }
  };

  const handleChangeStatus = async (next: CustomStrategyStatus) => {
    if (next === row.status) return;
    try {
      await StrategyService.updateStatus(row.strategy_id, next);
      await fetchDetail();
    } catch (err) {
      console.log(err);
      alert("상태 변경에 실패했습니다.");
    }
  };

  const handleToggleCheck = async (field: StrategyCheckField, current: "Y" | "N") => {
    const next = current !== "Y";
    try {
      await StrategyService.updateChecklist(row.strategy_id, field, next);
      await fetchDetail();
    } catch (err) {
      console.log(err);
      alert("처리 상태 변경에 실패했습니다.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3} sx={{ fontFamily: "Pretendard" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginBottom: 24,
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/management/strategy")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              backgroundColor: "white",
              fontSize: 13,
              fontWeight: 500,
              fontFamily: "Pretendard",
              cursor: "pointer",
            }}
          >
            <Icon fontSize="small">arrow_back</Icon>
            목록으로
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#111827",
                  fontFamily: "Pretendard",
                }}
              >
                맞춤전략 신청 상세
              </h2>
              <span
                style={{
                  display: "inline-block",
                  padding: "4px 10px",
                  borderRadius: 6,
                  backgroundColor: `${statusColor}1A`,
                  color: statusColor,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {statusLabel}
              </span>
            </div>
            <span style={{ fontSize: 13, color: "#6b7280" }}>
              No. {row.strategy_id} · {formatKST(row.create_time)} 신청
            </span>
          </div>
        </div>

        <Card sx={{ borderLeft: `4px solid #3b82f6`, mb: 2, p: "24px 28px" }}>
          <SectionTitle>고객 정보</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              rowGap: 20,
              columnGap: 32,
            }}
          >
            <Field label="No" value={String(row.strategy_id)} />
            <Field label="신청일" value={formatKST(row.create_time)} />
            <Field label="고객명" value={row.user_name ?? "-"} />
            <Field label="연락처" value={row.user_tel?.trim() || "-"} />
            <Field label="이메일" value={row.user_email ?? "-"} />
            <Field
              label="유입경로"
              value={row.channels && row.channels.length > 0 ? row.channels.join(", ") : "-"}
            />
          </div>
        </Card>

        <Card sx={{ borderLeft: `4px solid #a855f7`, mb: 2, p: "24px 28px" }}>
          <SectionTitle>신청 내용</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <Question num={1} text="관심있는 지역을 선택해 주세요." answers={row.regions} />
            <Question num={2} text="경매 참여의 목적을 선택해 주십시오." answers={row.purposes} />
            <Question
              num={3}
              text="어떤 자산에서 기회를 찾고 계신가요?"
              answers={
                row.asset_custom ? [...(row.asset_types ?? []), row.asset_custom] : row.asset_types
              }
            />
            <Question
              num={4}
              text="투자 가능 범위를 선택해 주십시오."
              answers={row.budget_range ? [row.budget_range] : null}
            />
            <Q4Block answer={row.service_type} />
          </div>
        </Card>

        <Card sx={{ borderLeft: `4px solid #f97316`, mb: 2, p: "24px 28px" }}>
          <SectionTitle>처리 현황 체크리스트</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {CHECK_LIST.map((item) => {
              const value = row[item.field];
              const done = value === "Y";
              return (
                <div
                  key={item.field}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 18px",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={done}
                    onChange={() => handleToggleCheck(item.field, value)}
                    style={{
                      width: 18,
                      height: 18,
                      cursor: "pointer",
                      accentColor: "#22c55e",
                    }}
                  />
                  <Icon style={{ fontSize: 22, color: item.iconColor }}>{item.icon}</Icon>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#111827",
                      flex: 1,
                      fontFamily: "Pretendard",
                    }}
                  >
                    {item.label}
                  </span>
                  <span
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor: done ? "#dcfce7" : "#f3f4f6",
                      color: done ? "#15803d" : "#6b7280",
                    }}
                  >
                    {done ? "완료" : "미완료"}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleToggleCheck(item.field, value)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 6,
                      border: "none",
                      backgroundColor: done ? "#9ca3af" : "#22c55e",
                      color: "white",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "Pretendard",
                    }}
                  >
                    {done ? "취소" : "완료 처리"}
                  </button>
                </div>
              );
            })}
          </div>
        </Card>

        <Card sx={{ borderLeft: `4px solid #22c55e`, mb: 2, p: "24px 28px" }}>
          <SectionTitle>상담 메모 및 처리</SectionTitle>

          <SubLabel>담당자 배정</SubLabel>
          <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
            <input
              type="text"
              value={assigneeInput}
              onChange={(e) => setAssigneeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAssign();
              }}
              placeholder="담당자 이름"
              style={{
                flex: "0 0 280px",
                padding: "10px 12px",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                fontSize: 14,
                fontFamily: "Pretendard",
              }}
            />
            <button
              type="button"
              onClick={handleAssign}
              style={{
                padding: "10px 22px",
                borderRadius: 8,
                border: "none",
                backgroundColor: "#2563eb",
                color: "white",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "Pretendard",
              }}
            >
              배정
            </button>
          </div>

          <SubLabel>처리 상태 변경</SubLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
            {STATUS_BUTTONS.map((s) => {
              const active = row.status === s;
              const palette = STATUS_BUTTON_COLOR[s];
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleChangeStatus(s)}
                  style={{
                    padding: "8px 18px",
                    borderRadius: 8,
                    border: active ? `2px solid ${palette.fg}` : "1px solid transparent",
                    backgroundColor: palette.bg,
                    color: palette.fg,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "Pretendard",
                  }}
                >
                  {CUSTOM_STRATEGY_STATUS_LABEL[s]}
                </button>
              );
            })}
          </div>

          <SubLabel>고객 상담 내용</SubLabel>
          <textarea
            value={memoInput}
            onChange={(e) => setMemoInput(e.target.value)}
            rows={4}
            style={{
              width: "100%",
              padding: "12px 14px",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              fontSize: 14,
              fontFamily: "Pretendard",
              resize: "vertical",
              marginBottom: 12,
              boxSizing: "border-box",
            }}
          />
          <button
            type="button"
            onClick={handleSaveMemo}
            style={{
              padding: "8px 22px",
              borderRadius: 8,
              border: "none",
              backgroundColor: "#1f2937",
              color: "white",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "Pretendard",
            }}
          >
            저장
          </button>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default StrategyDetail;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3
      style={{
        margin: 0,
        marginBottom: 20,
        fontSize: 16,
        fontWeight: 700,
        color: "#111827",
        fontFamily: "Pretendard",
      }}
    >
      {children}
    </h3>
  );
}

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: "#111827",
        marginBottom: 8,
        fontFamily: "Pretendard",
      }}
    >
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "Pretendard" }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: "#111827", fontFamily: "Pretendard" }}>
        {value}
      </span>
    </div>
  );
}

function Question({
  num,
  text,
  answers,
}: {
  num: 1 | 2 | 3 | 4;
  text: string;
  answers: string[] | null | undefined;
}) {
  const palette = QUESTION_CHIP_COLOR[num];
  const list = (answers ?? []).filter(Boolean);
  return (
    <div>
      <div
        style={{
          fontSize: 14,
          color: "#111827",
          fontWeight: 600,
          marginBottom: 8,
          fontFamily: "Pretendard",
        }}
      >
        Q{num}. {text}
      </div>
      {list.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {list.map((a) => (
            <span
              key={a}
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: 999,
                backgroundColor: palette.bg,
                color: palette.fg,
                fontSize: 13,
                fontWeight: 600,
                fontFamily: "Pretendard",
              }}
            >
              {a}
            </span>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 13, color: "#9ca3af", fontStyle: "italic" }}>답변 없음</span>
      )}
    </div>
  );
}

function Q4Block({ answer }: { answer: string | null | undefined }) {
  return (
    <div>
      <div
        style={{
          fontSize: 14,
          color: "#111827",
          fontWeight: 600,
          marginBottom: 8,
          fontFamily: "Pretendard",
        }}
      >
        Q5. 어떤 방식으로 결과를 만들고 싶으십니까?
      </div>
      <div
        style={{
          fontSize: 13,
          color: answer ? "#111827" : "#9ca3af",
          fontStyle: answer ? "normal" : "italic",
          fontFamily: "Pretendard",
        }}
      >
        {answer || "아직 결정하지 못했습니다"}
      </div>
    </div>
  );
}
